import { Document, DocumentType, GetDocumentsFolders200Item } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/i18n/LanguageContext';
import { Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';

interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (document: Omit<Document, 'id'>, file: File) => Promise<void>;
  onCancel: () => void;
  folders: GetDocumentsFolders200Item[];
}

export function UploadDocumentDialog({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  folders,
}: UploadDocumentDialogProps) {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'tax' as DocumentType,
    organization: '',
    year: new Date().getFullYear().toString(),
    folderId: null as number | null,
  });

  const documentTypes = [
    { value: 'tax', label: 'Tax Document' },
    { value: 'statement', label: 'Statement' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'legal', label: 'Legal Document' },
    { value: 'equity', label: 'Equity Document' },
    { value: 'retirement', label: 'Retirement' },
  ];

  const organizations = [
    'IRS',
    'Chase',
    'Fidelity',
    'Wells Fargo',
    'State Farm',
    'Geico',
    'Legal Zoom',
    'Northwestern Mutual',
    'County Recorder',
    'Vanguard',
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString());

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-populate name if not already set
      if (!formData.name) {
        const nameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
        setFormData({ ...formData, name: nameWithoutExtension });
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !selectedFile) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(
        {
          name: formData.name.trim(),
          type: formData.type,
          organization: formData.organization,
          year: formData.year,
          date: new Date().toISOString(),
          size: formatFileSize(selectedFile.size),
        },
        selectedFile
      );

      // Reset form
      setFormData({
        name: '',
        type: 'tax',
        organization: '',
        year: new Date().getFullYear().toString(),
        folderId: null,
      });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Failed to upload document:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      type: 'tax',
      organization: '',
      year: new Date().getFullYear().toString(),
      folderId: null,
    });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('vault.actions.upload')}</DialogTitle>
          <DialogDescription>Upload a new document to your secure vault.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="file">Document File</Label>
            {!selectedFile ? (
              <div className="border-muted-foreground/25 rounded-lg border-2 border-dashed p-6 text-center">
                <Upload className="text-muted-foreground mx-auto mb-2 h-8 w-8" />
                <p className="text-muted-foreground mb-2 text-sm">
                  Click to select a file or drag and drop
                </p>
                <Input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  className="hidden"
                  id="file-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Select File
                </Button>
              </div>
            ) : (
              <div className="bg-muted/50 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={handleRemoveFile}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Document Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Document Name</Label>
            <Input
              id="name"
              placeholder="Enter document name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Document Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value: DocumentType) => setFormData({ ...formData, type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Organization */}
          <div className="space-y-2">
            <Label htmlFor="organization">Organization</Label>
            <Select
              value={formData.organization}
              onValueChange={(value) => setFormData({ ...formData, organization: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org} value={org}>
                    {org}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year */}
          <div className="space-y-2">
            <Label htmlFor="year">Year</Label>
            <Select
              value={formData.year}
              onValueChange={(value) => setFormData({ ...formData, year: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Folder (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="folderId">Folder (Optional)</Label>
            <div className="flex gap-2">
              <Select
                value={formData.folderId?.toString() || undefined}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    folderId: value ? parseInt(value) : null,
                  })
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select folder (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {folders
                    .filter((folder) => folder.id != null)
                    .map((folder) => (
                      <SelectItem key={folder.id} value={folder.id!.toString()}>
                        {folder.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {formData.folderId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({ ...formData, folderId: null })}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting || !selectedFile}>
              {isSubmitting ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
